import { FC, useEffect, useState } from 'react';

import { LOCATION_STATE_FROM_PLACE_ID } from '@/constants/locationStates';
import { logError } from '@/utils/log';
import { toastError } from '@/utils/notification';
import {
  GeocoderResult,
  getAddressComponent,
  getFormattedAddressComponent
} from '@proprioo/hokkaido';

import { getLocationByLatLng } from '../fetch';
import GmapSearch from '../gmapSearch/GmapSearch';
import { LocationState } from '../interfaces';
import Tags from '../tags/Tags';
import {
  checkCoordinatesInsideFrancePolygon,
  checkCoordinatesInsideItalyPolygon,
  getLocationNameWithoutDistrict,
  getLocationType
} from '../utils';
import { GmapWrapper, Layout, TagsWrapper } from './LocationRefinement.styles';
import { getCountryFromAddress } from '@/components/qualification/projectForm/ProjectForm.utils';

export type LocationRefinementProps = {
  agentIds?: number[];
  locationState: LocationState[];
  update(locationState: LocationState[]): void;
  applyFilters?(): void;
};

const LocationRefinement: FC<LocationRefinementProps> = ({
  agentIds = [],
  locationState,
  applyFilters,
  update
}) => {
  const [temporaryLocationState, updateTemporaryLocationState] =
    useState<LocationState[]>(locationState);
  const [currentLocation, updateCurrentLocation] = useState<string>('');

  useEffect(() => {
    if (applyFilters) {
      applyFilters();
    }
  }, [temporaryLocationState]);

  const updateLocations = (locationState: LocationState[]) => {
    const locationStateWithoutDuplicate = locationState.reduce(
      (acc: LocationState[], location) => {
        const foundLocation = acc.find(({ id }) => id === location.id);

        if (!foundLocation) {
          acc.push(location);
        }

        return acc;
      },
      []
    );

    update(locationStateWithoutDuplicate);
    updateTemporaryLocationState(locationStateWithoutDuplicate);
  };

  const onSelect = async (addressComponents: GeocoderResult) => {
    const { address_components, types, place_id } = addressComponents;
    const {
      postalCode,
      location: { lat, lng }
    } = getFormattedAddressComponent(
      addressComponents,
      getCountryFromAddress(addressComponents.address_components)
    );

    console.log({ lat, lng });

    const country = getAddressComponent(address_components, 'country');

    const locationType = getLocationType(
      types,
      postalCode?.long_name,
      country?.short_name
    );

    const isValidLatLng =
      checkCoordinatesInsideFrancePolygon({
        lat,
        lng
      }) || checkCoordinatesInsideItalyPolygon({ lat, lng });

    if (isValidLatLng) {
      // This condition is handling place_id causing trouble
      if (LOCATION_STATE_FROM_PLACE_ID[place_id]) {
        const newLocationState: LocationState[] = [
          ...locationState,
          LOCATION_STATE_FROM_PLACE_ID[place_id]
        ];

        updateLocations(newLocationState);
      } else if (locationType) {
        try {
          const [currentLocation] = await getLocationByLatLng(
            lat,
            lng,
            locationType
          );

          if (currentLocation) {
            const { id, name, geom, type } = currentLocation;

            const nameWithoutDistrict = getLocationNameWithoutDistrict(name);

            const newLocationState: LocationState[] = [
              ...locationState,
              { geom, id, name: nameWithoutDistrict, type }
            ];

            updateLocations(newLocationState);
          } else {
            logError(
              `No location found by Geo: ${addressComponents.formatted_address}`,
              { addressComponents, lat, lng, locationType }
            );
            toastError('retrievingLocationsError');
          }
        } catch (error) {
          toastError('retrievingLocationsError');
        }
      } else {
        logError(`No location type found for ${postalCode?.long_name}`, {
          addressComponents,
          lat,
          lng,
          postalCode
        });
        toastError('retrievingLocationsError');
      }
    } else {
      toastError('doNotCurrentlyCoverThisArea');
    }

    updateCurrentLocation('');
  };

  return (
    <Layout>
      <TagsWrapper>
        <Tags
          agentIds={agentIds}
          locationState={locationState}
          updateLocations={updateLocations}
        />
        <GmapWrapper data-test="gmap-search">
          <GmapSearch
            value={currentLocation}
            onChange={updateCurrentLocation}
            onSelect={onSelect}
          />
        </GmapWrapper>
      </TagsWrapper>
    </Layout>
  );
};

export default LocationRefinement;
